import { Component, computed, input, output, signal, TemplateRef, viewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { LocationSelectors } from '@states/location/location.selector-types';
import { VehicleModels } from '@models/vehicle.model';
import { MatDialog } from '@angular/material/dialog';
import {
  UiVehicleDialogComponent,
  UiVehicleDialogData,
} from '../../dialogs/ui-vehicle-dialog/ui-vehicle-dialog.component';
import { ReactiveFormsModule } from '@angular/forms';
import { UiKitModule } from '../../../../../../shared/ui-kit/ui-kit.module';
import { UiVehicleIconComponent } from '../../../../../../shared/ui-kit/ui-vehicle-icon/ui-vehicle-icon.component';
import { SelectionChangeEvent, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createUnsavedVehiclesTableConfig, UnsavedVehicleCellContext } from './unsaved-vehicles-table.util';

@Component({
  selector: 'app-unsaved-vehicles-table',
  imports: [UiKitModule, ReactiveFormsModule, UiVehicleIconComponent, UiDatasetTableComponent],
  templateUrl: './unsaved-vehicles-table.component.html',
  styleUrl: './unsaved-vehicles-table.component.scss',
})
export class UnsavedVehiclesTableComponent {
  readonly unsavedVehicles = input.required<VehicleModels.Vehicle[]>();
  readonly selectable = input(false);
  readonly selectionChange = output<SelectionChangeEvent<VehicleModels.Vehicle>>();

  protected readonly imageCellTemplate = viewChild<TemplateRef<UnsavedVehicleCellContext>>('imageCell');

  private readonly locationNamesByEdgeId = this.store$.selectSignal(LocationSelectors.selectLocationNamesByEdgeId);

  public readonly selectedPlatesRows = signal<Set<string>>(new Set());
  public readonly forcedSelectedIds = signal<(string | number)[]>([]);

  public readonly tableConfig = computed(() => {
    const imageTemplate = this.imageCellTemplate();
    if (!imageTemplate) {
      return null;
    }

    return createUnsavedVehiclesTableConfig({
      imageCellTemplate: imageTemplate,
      getLocationName: (row) => this.getLocationNameFormatted(row),
      checkboxEnabled: this.selectable(),
      actionsEnabled: !this.selectable(),
    });
  });

  constructor(private store$: Store, private dialog: MatDialog) {}

  private getLocationNameFormatted(vehicle: VehicleModels.Vehicle): string {
    const namesMap = this.locationNamesByEdgeId();
    return namesMap?.[vehicle.edgeId] ?? '—';
  }

  public onSelectionChange(event: SelectionChangeEvent<VehicleModels.Vehicle>): void {
    const plates = new Set(event.selectedIds.map((id) => String(id)));
    this.selectedPlatesRows.set(plates);
    this.selectionChange.emit(event);
  }

  public add(vehicle?: VehicleModels.Vehicle) {
    const data: UiVehicleDialogData = {
      vehicle,
    };
    this.dialog
      .open(UiVehicleDialogComponent, {
        width: '540px',
        panelClass: 'modal-no-padding',
        data,
      });
  }

  public onRowAction(event: {
    actionId: string;
    row: VehicleModels.Vehicle;
  }): void {
    if (event.actionId === 'add') {
      this.add(event.row);
    }
  }
}
